import { useTranslation } from 'next-i18next';
import { useCallback, useContext } from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { generalSubscriptionsInput } from '../constants/subscriptions';
import { buildSignInFlow, buildSignUpFlow } from '../features/auth';
import { PhoneContainer } from '../features/auth/containers/PhoneContainer';
import { useSubscriptionsManager } from '../features/auth/hooks/useSubscriptionsManager';
import {
  AuthAnalytics,
  AuthEntryPoint,
  AuthFlow,
} from '../features/auth/types';
import { useAuth, useFlow } from '../providers';
import { AccountContext } from '../providers/AccountProvider';
import {
  GeneratedCreateSubscriptionsInput,
  IsSubscribedDocument,
} from '../types/generated';
import { ButtonLocation } from '../types/Subscriptions';
import { TrackEmailSubscription } from '../utils/analytics';
import { useAnalyticsEmitter } from '../utils/analytics/emitter';

interface AuthenticateOptions {
  subscriptions: GeneratedCreateSubscriptionsInput[];
  authScreenThumbnail?: string;
  optInGeneral?: boolean;
  authScreenTitle?: string;
  hideSuccessScreen?: boolean;
  analyticsProperties: {
    source: AuthEntryPoint;
    'button_location': ButtonLocation;
  } & Record<string, string>;
  flowType?: AuthFlow;
  onFlowComplete?: () => void;
  showPhoneStep?: boolean;
  showOnboarding?: boolean;
}

export const useAuthenticate = () => {
  const { t } = useTranslation('authentication');
  const [emitter] = useAnalyticsEmitter();
  const { user } = useAuth();
  const { startFlow } = useFlow();
  const { subscribe } = useSubscriptionsManager();
  const { onboarding } = useContext(AccountContext);

  const startAuthFlow = ({
    analyticsProperties,
    authScreenThumbnail,
    authScreenTitle,
    flowType,
    hideSuccessScreen,
    onFlowComplete,
    optInGeneral,
    showPhoneStep,
    subscriptions,
  }: AuthenticateOptions) => {
    if (flowType === AuthFlow.SignIn) {
      startFlow({
        name: AuthFlow.SignIn,
        steps: buildSignInFlow({
          subscriptions,
          optInGeneral,
          showPhoneStep,
        }),
        analyticsProperties,
        onFlowComplete,
      });
    } else {
      startFlow({
        name: AuthFlow.SignUp,
        steps: buildSignUpFlow({
          subscriptions,
          imageUrl: authScreenThumbnail,
          title: authScreenTitle,
          optInGeneral,
          hideSuccessScreen,
          showPhoneStep,
        }),
        analyticsProperties,
        onFlowComplete,
      });
    }
  };

  const authenticateAndSubscribe = useCallback(
    async ({
      analyticsProperties,
      authScreenThumbnail,
      authScreenTitle,
      flowType = AuthFlow.SignUp,
      hideSuccessScreen = false,
      onFlowComplete,
      optInGeneral = true,
      showOnboarding = false,
      showPhoneStep = false,
      subscriptions: subscriptionsInput,
    }: AuthenticateOptions) => {
      const subscriptions = optInGeneral
        ? [generalSubscriptionsInput, ...subscriptionsInput]
        : subscriptionsInput;

      const enhancedAnalytics = {
        ...analyticsProperties,
        is_signed_in: !!user,
      };

      emitter('subscribe', 'clicked', enhancedAnalytics, {
        omitObject: true,
      });

      // If already logged in, subscribe user
      if (user) {
        if (showPhoneStep && user.phone === null) {
          startFlow({
            name: AuthFlow.PhoneOnly,
            steps: [
              {
                id: 'phone',
                content: <PhoneContainer phone={user.phone || undefined} />,
                title: t('screens.phone.title'),
                analytics: {
                  name: AuthAnalytics.SignInPhone,
                },
              },
            ],
          });
        }

        if (!onboarding?.isOnboardingStarted && showOnboarding) {
          onboarding?.show({
            page: analyticsProperties.source,
            id:
              analyticsProperties.artist_id ||
              analyticsProperties.product_id ||
              analyticsProperties.subscription_id,
          });
        }

        emitter('subscribe', 'submit', enhancedAnalytics, {
          omitObject: true,
        });

        const success = await subscribe(subscriptions);
        if (success) {
          csrClient.refetchQueries({ include: [IsSubscribedDocument] });
          TrackEmailSubscription({
            artistName: analyticsProperties['artist name'],
            artistId: analyticsProperties.artist_id,
            emailAddress: user.email || '',
            accountId: user.id,
            releaseName: analyticsProperties.subscription_name,
            productId: analyticsProperties.product_id,
            productTitle: analyticsProperties.product_title,
            mode: 'GENERAL',
            trackEvent: false,
            type: 'success',
          });
          emitter('subscribe', 'success', enhancedAnalytics, {
            omitObject: true,
          });
        }
        return;
      }

      // Otherwise, start authentication flow
      startAuthFlow({
        flowType,
        subscriptions,
        optInGeneral,
        authScreenThumbnail,
        authScreenTitle,
        hideSuccessScreen,
        analyticsProperties,
        onFlowComplete,
        showPhoneStep,
      });
    },
    [emitter, onboarding, startAuthFlow, startFlow, subscribe, t, user],
  );

  const authenticate = useCallback(
    ({
      analyticsProperties,
      authScreenThumbnail,
      authScreenTitle,
      flowType = AuthFlow.SignUp,
      hideSuccessScreen = false,
      optInGeneral = true,
    }: Omit<AuthenticateOptions, 'subscriptions'>) => {
      // If already logged in, do nothing
      if (user) {
        return;
      }

      // Otherwise, start authentication flow
      startAuthFlow({
        flowType,
        subscriptions: [],
        optInGeneral,
        authScreenThumbnail,
        authScreenTitle,
        hideSuccessScreen,
        analyticsProperties,
      });
    },
    [user],
  );

  return { authenticate, authenticateAndSubscribe };
};
